import { HttpErrorResponse } from '@angular/common/http';

export type ErpErrorKind =
  | 'business'
  | 'validation'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'rate-limit'
  | 'timeout'
  | 'offline'
  | 'server'
  | 'unexpected';

export class ErpApiError extends Error {
  constructor(
    readonly kind: ErpErrorKind,
    readonly status: number,
    message?: string,
    readonly retryAfterSeconds?: number,
  ) {
    super(message ?? erpErrorMessage(kind, retryAfterSeconds));
    this.name = 'ErpApiError';
  }
}

export function mapErpError(error: unknown): ErpApiError {
  if (error instanceof ErpApiError) {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return new ErpApiError(
        typeof navigator !== 'undefined' && !navigator.onLine
          ? 'offline'
          : 'unexpected',
        error.status,
      );
    }
    if (error.status === 400 || error.status === 403 || error.status === 404) {
      const businessMessage = readBusinessMessage(error.error);
      if (businessMessage) {
        return new ErpApiError('business', error.status, businessMessage);
      }
      if (error.status === 400) return new ErpApiError('validation', 400);
      if (error.status === 403) return new ErpApiError('forbidden', 403);
      return new ErpApiError('not-found', 404);
    }
    if (error.status === 401) {
      return new ErpApiError('unauthorized', 401);
    }
    if (error.status === 409) {
      return new ErpApiError('conflict', 409);
    }
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      return new ErpApiError(
        'rate-limit',
        429,
        undefined,
        Number.isFinite(retryAfter) ? retryAfter : undefined,
      );
    }
    if (error.status >= 500) {
      return new ErpApiError('server', error.status);
    }
  }

  if (error instanceof Error && error.name === 'TimeoutError') {
    return new ErpApiError('timeout', 0);
  }

  return new ErpApiError('unexpected', 0);
}

function readBusinessMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object' || !('error' in body)) {
    return null;
  }
  const message = (body as { error?: unknown }).error;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}

function erpErrorMessage(
  kind: ErpErrorKind,
  retryAfterSeconds?: number,
): string {
  switch (kind) {
    case 'business':
    case 'validation':
      return 'Revisa los datos e intenta de nuevo.';
    case 'unauthorized':
      return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    case 'forbidden':
      return 'No tienes permiso para realizar esta acción.';
    case 'not-found':
      return 'El registro no existe o ya no está disponible.';
    case 'conflict':
      return 'Ya existe un registro con estos datos.';
    case 'rate-limit':
      return retryAfterSeconds
        ? `Demasiadas solicitudes. Intenta de nuevo en ${retryAfterSeconds} segundos.`
        : 'Demasiadas solicitudes. Espera un momento antes de volver a intentar.';
    case 'timeout':
      return 'La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.';
    case 'offline':
      return 'No hay conexión a internet. Verifica tu red e intenta de nuevo.';
    case 'server':
    case 'unexpected':
      return 'No fue posible completar la solicitud. Intenta de nuevo más tarde.';
  }
}
